import { useEffect, useRef } from 'react';
import { PRIORITY, useKeys } from '../lib/keyboard.js';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Footer hint, e.g. "Ctrl+Enter save". */
  hint?: React.ReactNode;
  width?: 'sm' | 'md';
}

/** Centered dialog above the panel. Esc closes; Enter/Ctrl+Enter are handled by the content. */
export function Modal({ title, onClose, children, hint, width = 'sm' }: ModalProps) {
  useKeys({ Escape: onClose }, { priority: PRIORITY.picker });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>('[data-autofocus]');
    first?.focus();
  }, []);
  return (
    <>
      <div className="fixed inset-0 z-30 bg-ink/30" onMouseDown={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-label={title}
        className={`fixed left-1/2 top-24 max-md:top-12 z-40 ${width === 'md' ? 'w-[min(640px,calc(100vw-32px))]' : 'w-[min(440px,calc(100vw-32px))]'} -translate-x-1/2 rounded-2xl border border-line bg-paper p-5 shadow-2xl max-h-[calc(100vh-7rem)] overflow-y-auto`}
      >
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted">{title}</h2>
        {children}
        {hint && <div className="mt-3 text-[11px] text-muted">{hint}</div>}
      </div>
    </>
  );
}
